import type {
    ActKc2StatusEnum,
    BimActKc2OtherStatusEnum,
    ContractChangeStatusEnum,
    Maybe,
    ProjectStageEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import {
    AddRatioToActKc2StrategyEnum,
    CostGroupEnum,
    ProjectContractTypeEnum,
    ProjectMethodologyEnum,
    WorkOrderChangeStatusEnum,
    WorkOrderPriceTypeEnum,
} from 'gql/graphql';
import { CreatingTypes } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { ActKC2CostGroup, ActKC2StatusLabels } from 'pages/Project/Common/types';
import { statusProps } from 'pages/Project/UerProject/AgreementsRegistry/AgreementsStatus';
import { WorkOrderStatusLabels } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';

import { useMemo, useState } from 'react';

import { useProfileInfo } from './useProfileInfo';

const TranslatedMethodology = {
    [ProjectMethodologyEnum.Uer]: 'УЕР',
    [ProjectMethodologyEnum.Bim]: 'БИМ',
    [ProjectMethodologyEnum.Etr]: 'ЕТР',
    [ProjectMethodologyEnum.None]: 'N/A',
};

export const workOrderChangeStatusLabel = {
    [WorkOrderChangeStatusEnum.OnFormation]: 'На формировании',
    [WorkOrderChangeStatusEnum.Approving]: 'На согласовании',
    [WorkOrderChangeStatusEnum.Approved]: 'Согласован',
};

const AddType = {
    [WorkOrderPriceTypeEnum.Uer]: 'Расценка',
    [WorkOrderPriceTypeEnum.Mtr]: 'МТР',
    [WorkOrderPriceTypeEnum.Coefficient]: 'Коэффициенты',
    [CostGroupEnum.Others]: 'Прочие затраты',
};

const AddCoefficientAction = {
    [AddRatioToActKc2StrategyEnum.DuplicateWithExtendedCoefficients]: 'Дублирование расценки',
    [AddRatioToActKc2StrategyEnum.CorrectOriginal]: 'Корректировка исходной',
};

const AddOthersAction = {
    [CreatingTypes.BOOK]: 'Из справочника методик расчета',
    [CreatingTypes.MANUAL]: 'Вручную',
};

const ProjectType = {
    [ProjectContractTypeEnum.Frame]: 'Рамочный',
    [ProjectContractTypeEnum.Normal]: 'Обычный',
};

type ParamsType = { componentid: string; constaeventtype: string; component: string; customparams?: any[] | undefined };

export const useSendGpnAnalyticsEvent = () => {
    const [canSend, setCanSend] = useState(true);

    const { organisation, userName, role } = useProfileInfo();

    const sendGpnAnalyticsEvent = useMemo(
        () =>
            ({
                componentId,
                constaeventtype = 'click',
                component = 'BUTTON',
                customUserName = undefined,
                customOrganisation = undefined,
                customRole = undefined,
                projectId = undefined,
                projectName = undefined,
                projectType = undefined,
                projectStage = undefined,
                projectDO = undefined,
                calculationId = undefined,
                contractId = undefined,
                orderId = undefined,
                agreementId = undefined,
                methodology = undefined,
                orderStatus,
                orderChangeStatus,
                agreementStatus,
                actKC2Status,
                sendOnce = false,
                costGroup,
                actKC2Id,
                KC3Id,
                addType,
                orderChangeId,
                addCoefficientActionType,
                addOtherActionType,
                extraParams = [],
            }: {
                componentId: string;
                constaeventtype?: string;
                component?: string;
                customUserName?: string;
                customOrganisation?: string;
                customRole?: string;
                methodology?: ProjectMethodologyEnum;
                projectId?: string;
                projectName?: string;
                projectType?: Maybe<ProjectContractTypeEnum>;
                projectStage?: ProjectStageEnum;
                projectDO?: string;
                calculationId?: number;
                contractId?: number;
                agreementId?: number;
                orderId?: number;
                orderStatus?: WorkOrderStatusEnum;
                orderChangeStatus?: WorkOrderChangeStatusEnum;
                sendOnce?: boolean;
                costGroup?: CostGroupEnum;
                actKC2Status?: ActKc2StatusEnum | BimActKc2OtherStatusEnum;
                agreementStatus?: ContractChangeStatusEnum;
                actKC2Id?: number;
                KC3Id?: number;
                orderChangeId?: number;
                addType?: keyof typeof AddType;
                addCoefficientActionType?: keyof typeof AddCoefficientAction;
                addOtherActionType?: keyof typeof AddOthersAction;
                extraParams?: Array<{ name: string; type: 'STRING' | 'INT'; value: any }>;
            }) => {
                const params: ParamsType = {
                    componentid: componentId,
                    constaeventtype,
                    component,
                };
                const userNameForParams = customUserName ?? userName;
                const organisationForParams = customOrganisation ?? organisation;
                const roleForParams = customRole ?? role;

                if (userNameForParams || organisation || role) {
                    params.customparams = [];

                    if (userNameForParams) {
                        params.customparams.push(
                            {
                                as_user_id: true,
                                value: userNameForParams.toLowerCase(),
                            },
                            { name: 'user_id', type: 'STRING', value: userNameForParams.toLowerCase() },
                        );
                    }
                    if (organisationForParams) {
                        params.customparams.push({
                            name: 'organisation',
                            type: 'STRING',
                            value: organisationForParams,
                        });
                    }
                    if (roleForParams) {
                        params.customparams.push({ name: 'role', type: 'STRING', value: roleForParams });
                    }
                    if (projectId) {
                        params.customparams.push({ name: 'project_id', type: 'STRING', value: projectId });
                    }
                    if (projectName) {
                        params.customparams.push({ name: 'project_name', type: 'STRING', value: projectName });
                    }
                    if (projectType) {
                        params.customparams.push({
                            name: 'project_type',
                            type: 'STRING',
                            value: ProjectType[projectType],
                        });
                    }
                    if (projectDO) {
                        params.customparams.push({
                            name: 'project_DO',
                            type: 'STRING',
                            value: projectDO,
                        });
                    }
                    if (projectStage) {
                        params.customparams.push({
                            name: 'project_stage',
                            type: 'STRING',
                            value: projectStage,
                        });
                    }
                    if (contractId) {
                        params.customparams.push({ name: 'contract_id', type: 'INT', value: contractId });
                    }
                    if (calculationId) {
                        params.customparams.push({ name: 'calculation_id', type: 'INT', value: calculationId });
                    }
                    if (orderId) {
                        params.customparams.push({ name: 'order_id', type: 'INT', value: orderId });
                    }
                    if (actKC2Id) {
                        params.customparams.push({ name: 'ks2_id', type: 'INT', value: actKC2Id });
                    }
                    if (KC3Id) {
                        params.customparams.push({ name: 'ks3_id', type: 'INT', value: KC3Id });
                    }
                    if (agreementId) {
                        params.customparams.push({ name: 'DS_id', type: 'INT', value: agreementId });
                    }
                    if (orderChangeId) {
                        params.customparams.push({ name: 'IZM_id', type: 'INT', value: orderChangeId });
                    }
                    if (methodology) {
                        params.customparams.push({
                            name: 'methodology',
                            type: 'STRING',
                            value: TranslatedMethodology[methodology],
                        });
                    }
                    if (orderStatus) {
                        params.customparams.push({
                            name: 'order_status',
                            type: 'STRING',
                            value: WorkOrderStatusLabels[orderStatus],
                        });
                    }
                    if (costGroup) {
                        params.customparams.push({
                            name: 'cost_group',
                            type: 'STRING',
                            value: ActKC2CostGroup[costGroup],
                        });
                    }
                    if (actKC2Status) {
                        params.customparams.push({
                            name: 'ks2_status',
                            type: 'STRING',
                            value: ActKC2StatusLabels[actKC2Status],
                        });
                    }
                    if (agreementStatus) {
                        params.customparams.push({
                            name: 'DS_status',
                            type: 'STRING',
                            value: statusProps[agreementStatus].label,
                        });
                    }
                    if (orderChangeStatus) {
                        params.customparams.push({
                            name: 'IZM_status',
                            type: 'STRING',
                            value: workOrderChangeStatusLabel[orderChangeStatus],
                        });
                    }
                    if (addType) {
                        params.customparams.push({
                            name: 'add_type',
                            type: 'STRING',
                            value: AddType[addType],
                        });
                    }
                    if (addCoefficientActionType) {
                        params.customparams.push({
                            name: 'action_type',
                            type: 'STRING',
                            value: AddCoefficientAction[addCoefficientActionType],
                        });
                    }
                    if (addOtherActionType) {
                        params.customparams.push({
                            name: 'other_create_type',
                            type: 'STRING',
                            value: AddOthersAction[addOtherActionType],
                        });
                    }

                    params.customparams = [...params.customparams, ...extraParams];
                }

                const sendMsg = () => {
                    (
                        window as Window & {
                            gpnAnalytics?: {
                                sendEvent: (n: number, params: ParamsType) => void;
                            };
                        }
                    ).gpnAnalytics?.sendEvent?.(3, params);

                    console.log('gpnAnalytics event params sent', params);
                };

                if (!sendOnce) {
                    sendMsg();
                } else if (sendOnce && canSend) {
                    sendMsg();
                    setCanSend(false);
                }
            },
        [canSend, organisation, role, userName],
    );

    const analyticsLoginEventManager = useMemo(
        () => ({
            checkIsNotDispatched: () => !sessionStorage.getItem('analyticsEventDispatched'),
            saveStatus: () => {
                sessionStorage.setItem('analyticsEventDispatched', 'Dispatched');
            },
            clearStatus: () => {
                sessionStorage.removeItem('analyticsEventDispatched');
            },
        }),
        [],
    );

    return {
        sendGpnAnalyticsEvent,
        analyticsLoginEventManager,
    };
};
